import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

// 🔹 Reusable Try It Button
function TryItButton({ code }) {
  const handleClick = () => {
    const encodedCode = encodeURIComponent(code);
    window.open(`/editorJS?code=${encodedCode}`, "_blank");
  };

  return (
    <button onClick={handleClick} className="try_it_button">
      ▶ Try It
    </button>
  );
}

export default function JS_Operators() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">➕ JavaScript Operators</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        JavaScript operators are symbols that perform operations on values and
        variables. They are used to assign, compare, and manipulate data.
      </p>

      {/* Key Points */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #f0f9ff, #e0f2fe)",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <b>Arithmetic Operators</b> → perform math operations.
          </li>
          <li>
            <b>Assignment Operators</b> → assign values to variables.
          </li>
          <li>
            <b>Comparison Operators</b> → compare two values.
          </li>
          <li>
            <b>Logical Operators</b> → combine multiple conditions.
          </li>
          <li>
            <b>String, Ternary, and Type Operators</b> → perform advanced
            operations.
          </li>
        </ul>
      </div>

      {/* Example 1: Arithmetic Operators */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Arithmetic Operators</h2>
        <p>
          Arithmetic operators perform mathematical calculations like addition,
          subtraction, multiplication, and division.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let x = 10;
let y = 5;
console.log("Addition:", x + y);
console.log("Subtraction:", x - y);
console.log("Multiplication:", x * y);
console.log("Division:", x / y);`}
          />
          <TryItButton
            code={`let x = 10;
let y = 5;
console.log("Addition:", x + y);
console.log("Subtraction:", x - y);
console.log("Multiplication:", x * y);
console.log("Division:", x / y);`}
          />
        </div>
        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #2563eb",
            borderRadius: "8px",
          }}
        >
          Output:
          <pre>
            Addition: 15
            <br />
            Subtraction: 5
            <br />
            Multiplication: 50
            <br />
            Division: 2
          </pre>
        </div>
      </div>

      {/* Example 2: Assignment Operators */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Assignment Operators</h2>
        <p>Assignment operators assign values to variables efficiently.</p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let x = 10;
x += 5; // x = x + 5
x -= 3; // x = x - 3
console.log(x);`}
          />
          <TryItButton
            code={`let x = 10;
x += 5;
x -= 3;
console.log(x);`}
          />
        </div>
        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #16a34a",
            borderRadius: "8px",
          }}
        >
          Output: <b>12</b>
        </div>
      </div>

      {/* Example 3: Comparison Operators */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Comparison Operators</h2>
        <p>
          These operators compare two values and return a boolean (
          <code>true</code> or <code>false</code>).
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let a = 10;
let b = 5;
console.log(a > b);  // true
console.log(a === b); // false`}
          />
          <TryItButton
            code={`let a = 10;
let b = 5;
console.log(a > b);
console.log(a === b);`}
          />
        </div>
        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #9333ea",
            borderRadius: "8px",
          }}
        >
          Output:
          <pre>
            true
            <br />
            false
          </pre>
        </div>
      </div>

      {/* Example 4: Logical Operators */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ Logical Operators</h2>
        <p>Combine multiple conditions using logical operators.</p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let age = 20;
console.log(age > 18 && age < 30); // true
console.log(age > 25 || age < 18); // false
console.log(!(age === 20));        // false`}
          />
          <TryItButton
            code={`let age = 20;
console.log(age > 18 && age < 30);
console.log(age > 25 || age < 18);
console.log(!(age === 20));`}
          />
        </div>
        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #f59e0b",
            borderRadius: "8px",
          }}
        >
          Output:
          <pre>
            true
            <br />
            false
            <br />
            false
          </pre>
        </div>
      </div>

      {/* Example 5: String Operators */}
      <div className="tutorial-section">
        <h2 className="section-title">5️⃣ String Operators</h2>
        <p>
          The <code>+</code> operator concatenates (joins) strings together.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let firstName = "Edu-Tech";
let lastName = "Master Academy";
let fullName = firstName + " " + lastName;
console.log(fullName);`}
          />
          <TryItButton
            code={`let firstName = "Edu-Tech";
let lastName = "Master Academy";
let fullName = firstName + " " + lastName;
console.log(fullName);`}
          />
        </div>
        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #3b82f6",
            borderRadius: "8px",
          }}
        >
          Output: <b>Edu-Tech Master Academy</b>
        </div>
      </div>

      {/* Example 6: Ternary Operator */}
      <div className="tutorial-section">
        <h2 className="section-title">6️⃣ Ternary Operator</h2>
        <p>
          A shorthand way to write <code>if...else</code> statements.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let age = 18;
let message = age >= 18 ? "You are an adult." : "You are a minor.";
console.log(message);`}
          />
          <TryItButton
            code={`let age = 18;
let message = age >= 18 ? "You are an adult." : "You are a minor.";
console.log(message);`}
          />
        </div>
        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #22c55e",
            borderRadius: "8px",
          }}
        >
          Output: <b>You are an adult.</b>
        </div>
      </div>

      {/* Example 7: Type Operators */}
      <div className="tutorial-section">
        <h2 className="section-title">7️⃣ Type Operators</h2>
        <p>
          The <code>typeof</code> and <code>instanceof</code> operators check
          the type of a variable or its constructor.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let name = "Edu-Tech Master Academy";
let num = 42;
console.log(typeof name); // string
console.log(typeof num); // number`}
          />
          <TryItButton
            code={`let name = "Edu-Tech Master Academy";
let num = 42;
console.log(typeof name);
console.log(typeof num);`}
          />
        </div>
        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #0ea5e9",
            borderRadius: "8px",
          }}
        >
          Output:
          <pre>
            string
            <br />
            number
          </pre>
        </div>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Learn about <b>JavaScript Data Types</b>.
          </li>
          <li>
            Understand <b>Conditional Statements</b> like if-else and switch.
          </li>
          <li>Practice using operators in small projects.</li>
        </ul>
      </div>
    </div>
  );
}
